// AdminSettingsPage — /admin/settings：**每个步骤调用 LLM 时用哪个模型**
// （SB-068 / SB-077 / SB-084，第六十八批）。
//
// ── 为什么这一页到第六十八批才出现 ─────────────────────────────────────────
// 后端从第六十三批起就齐了：写端点（POST /api/admin/settings/llm-operation）、
// 白名单判据、型号校验、admin 闸、部署不抹（OQ-B28 二期分键裁定）、单测。
// **缺的一直只是这个页面**——它原定归宿是 SB-011 管理控制台，而 SB-011 自己
// 阻塞着。于是"改模型"这件事在能力上早就存在，在人手里却一次都没存在过。
// 需求方第六十八批：「我目前还不能在页面中更改系统在各步骤调用LLM时所使用的模型」。
//
// ── 这一页刻意显示三个值而不是一个 ─────────────────────────────────────────
// 部署值 / 覆盖值 / 实际生效值。因为它们会不一致，而不一致时**最危险的一态是
// "覆盖存着但没生效"**（模型已不在 modelMappings 里，resolve 静静回退到部署值）：
// 只显示生效值的话，admin 看到的是部署值，会以为自己没保存成功，于是再设一次，
// 再被忽略一次——两次的表现完全一样。后端专门投影了 overrideIgnored 这个布尔，
// 而在本页出现之前**没有任何东西渲染它**。
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import ReportSettingsPanel from '../components/ReportSettingsPanel.jsx';
import {
  REQUEUE_ERR_KEYS, catalogFreshness, failClassKey, failClassOf, isUnwired, needsWrite,
  optionsFor, overrideState, requeueNeedsForce, sortOperations, spendView,
} from './adminSettings.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { workHoursFormState } from './groupCreate.mjs';

const STATE_COLOR = { default: 'default', override: 'primary', ignored: 'warning' };

export default function AdminSettingsPage() {
  const t = useT();
  const { lang } = useLang();
  const { snack, notify, clearSnack } = useNotify();
  const load = useLoad(() => api.adminSettings(), []);
  const [busyOp, setBusyOp] = useState('');
  const [draft, setDraft] = useState({});   // op → 待提交的型号（'' = 撤销覆盖）

  const ops = useMemo(() => sortOperations(load.data?.operations), [load.data]);
  const models = useMemo(
    () => (Array.isArray(load.data?.availableModels) ? load.data.availableModels : []),
    [load.data],
  );
  // 载入后把草稿对齐到当前覆盖值，否则下拉框会显示空白而不是现状。
  useEffect(() => {
    if (!ops.length) return;
    setDraft(Object.fromEntries(ops.map((o) => [o.op, o.overrideValue ?? ''])));
  }, [ops]);

  const fresh = catalogFreshness(load.data?.catalogFetchedAtMs ?? null, Date.now());

  const apply = async (o) => {
    const next = draft[o.op] ?? '';
    if (busyOp || !needsWrite(o, next)) return;
    setBusyOp(o.op);
    try {
      await api.adminSetLlmOperationModel(o.op, next);
      notify(t(next ? 'admin.settings.saved' : 'admin.settings.cleared'), 'success');
      load.reload();
    } catch (e) {
      // 400 带上后端的拒因原文：那句话里写着"为什么这个型号不行"
      // （未分类/无结构化/无视觉/目录里没有），吞掉它就只剩"保存失败"——
      // 需求方第六十九批撞到的正是那个没有信息量的提示。
      const detail = e?.status === 400 && e?.message ? '：' + e.message : '';
      notify(t(errKey(e, { byStatus: { 400: 'admin.settings.invalidModel' },
                           fallback: 'admin.settings.error' })) + detail, 'error');
    } finally {
      setBusyOp('');
    }
  };

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        {t('admin.settings.title')}
        <HelpLink section="admin" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.settings.intro')}
      </Typography>

      {/* 第三百一十三批（SB-202 S3）：资方简报的项目层设置也挂在这一页顶部——需求方在「设置」页找它而没找到；
          与 /admin「账号」页项目组表里的那一枚是同一只组件、同一条端点。 */}
      <Box sx={{ mb: 3 }}>
        <ReportSettingsPanel t={t} lang={lang} notify={notify} />
      </Box>

      {load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(load.error, { fallback: 'admin.settings.error' }))}
          </Alert>
          <Button size="small" onClick={load.reload}>{t('common.retry')}</Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1}>
            <Chip size="small" label={`${t('admin.settings.provider')}: ${load.data?.activeProvider}`} />
            {/* 目录的出处必须显示：一个不知道多久没刷新的下拉框，与一份手工维护的
                清单没有区别——而消灭手工清单正是 SB-084 的全部意义。 */}
            <Chip
              size="small"
              color={fresh.state === 'fresh' ? 'success' : fresh.state === 'stale' ? 'warning' : 'default'}
              variant="outlined"
              label={t(`admin.settings.catalog.${fresh.state}`)}
            />
            <Typography variant="caption" color="text.secondary">
              {t('admin.settings.catalog.count', { n: models.length })}
            </Typography>
          </Stack>

          {/* 260px 下拉列 + 两列 nowrap 模型 id：min-content 早已超过平板宽——minWidth 接管 */}
          <DataTable minWidth={900}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.settings.col.op')}</TableCell>
                  <TableCell>{t('admin.settings.col.configModel')}</TableCell>
                  <TableCell>{t('admin.settings.col.override')}</TableCell>
                  <TableCell>{t('admin.settings.col.effective')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {ops.map((o) => {
                  const state = overrideState(o);
                  const opts = optionsFor(models, o);
                  return (
                    <TableRow key={o.op}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack spacing={0.25}>
                          <Stack direction="row" spacing={0.75} useFlexGap
                                 sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 600 }} variant="body2">
                              {t(`admin.settings.op.${o.op}`) === `admin.settings.op.${o.op}`
                                ? o.op : t(`admin.settings.op.${o.op}`)}
                            </Typography>
                            {/* ── 第一百一十批：空壳能力的标注 ────────────────────
                                `asset_review` 与 `chat` 在这一页上看起来像"可配置、
                                在跑"的能力——它们不是（前者全仓零调用方；后者唯一路由
                                /llm/echo 是 SB-001 时代的冒烟路由，职位配置里的
                                chat_rounds_per_day 后端一个字都没读）。风险不是"有个
                                没用的配置项"，是**有人会去调它然后等着看效果**，
                                包括正在排查故障的人。
                                判据取服务端投影的 `wired` 位（由 defaults.h 推出），
                                不是前端硬编码的名单（那就是 DP-2）。
                                ⚠ 文案必须逐字写"全仓没有任何调用方，改它不会有任何
                                效果"，而不是"未启用"——后者会被读成"暂时关掉了"，
                                于是有人去找开关。 */}
                            {isUnwired(o) ? (
                              <Tooltip title={t('admin.settings.op.unwiredHint')}>
                                <Chip size="small" variant="outlined" color="warning"
                                      label={t('admin.settings.op.unwired')} />
                              </Tooltip>
                            ) : null}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {o.op} · {o.kind}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>
                        {o.configModel}
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={draft[o.op] ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, [o.op]: e.target.value }))}
                          disabled={Boolean(busyOp)}
                        >
                          {/* 空值 = 撤销覆盖、回到部署值。它必须是一个**明确的选项**，
                              而不是"把框清空"——后者与"还没选"分不开。 */}
                          <MenuItem value="">{t('admin.settings.useDeploy')}</MenuItem>
                          {opts.map((m) => (
                            // whiteSpace normal + wrap：Popover 纸面横向只裁不滚，nowrap 的
                            // 长模型 id + 多枚 Chip 在窄屏上会被静默裁掉——裁掉的恰是
                            // 「为什么不可选」那枚说明 Chip
                            <MenuItem key={m.id} value={m.id} disabled={!m.selectable}
                                      sx={{ whiteSpace: 'normal' }}>
                              <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1} useFlexGap>
                                <span style={{ fontFamily: 'ui-monospace, monospace' }}>{m.id}</span>
                                <Chip size="small" variant="outlined" label={m.provider} />
                                {m.source === 'discovered' ? (
                                  <Chip size="small" variant="outlined" color="info"
                                        label={t('admin.settings.src.discovered')} />
                                ) : null}
                                {!m.selectable ? (
                                  <Chip size="small" variant="outlined" color="warning"
                                        label={t(`admin.settings.why.${m.reason}`)} />
                                ) : null}
                              </Stack>
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
                          <span style={{ fontFamily: 'ui-monospace, monospace' }}>
                            {o.effectiveModel}
                          </span>
                          <Tooltip title={t(`admin.settings.state.${state}.hint`)}>
                            <Chip size="small" color={STATE_COLOR[state]} variant="outlined"
                                  label={t(`admin.settings.state.${state}`)} />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={Boolean(busyOp) || !needsWrite(o, draft[o.op] ?? '')}
                          onClick={() => apply(o)}
                        >
                          {t('common.save')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
          </DataTable>

          {/* ── 第八十一批：作业队列 ────────────────────────────────────────
              后端从第六十五批起就投影了四个计数，而**没有任何页面渲染过它们**
              （与本批 /admin/logs 同型的可达性缺口）。四个计数本身也不够：
              routes_admin.cpp 那处注释自己写着"没有它『译文没出来』这句话不可诊断"，
              而 failed=1 指不出任何一个可执行的下一步——是模型超时、是提示词被拒、
              还是入参有问题？三者的处置完全不同。所以本批一并投影 last_error 与
              attempts，并在这里显示出来。 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('admin.settings.jobs.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('admin.settings.jobs.intro')}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }}>
              {['pending', 'running', 'done', 'failed'].map((s) => (
                <Chip
                  key={s}
                  size="small"
                  color={s === 'failed' && Number(load.data?.jobs?.[s]) > 0 ? 'error' : 'default'}
                  variant={s === 'failed' && Number(load.data?.jobs?.[s]) > 0 ? 'filled' : 'outlined'}
                  label={`${t(`admin.settings.jobs.state.${s}`)}: ${load.data?.jobs?.[s] ?? 0}`}
                />
              ))}
            </Stack>
            {(load.data?.jobs?.failedRows ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('admin.settings.jobs.noFailures')}
              </Typography>
            ) : (
              // lastError ~150ch 单元格实测折成 15.3 行高塔——minWidth 让它横向铺开
              <DataTable minWidth={760}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('admin.settings.jobs.col.job')}</TableCell>
                      <TableCell>{t('admin.settings.jobs.col.entity')}</TableCell>
                      <TableCell>{t('admin.settings.jobs.col.attempts')}</TableCell>
                      <TableCell>{t('admin.settings.jobs.col.lastError')}</TableCell>
                      {/* 重排列无表头：动作列，同上面那张模型表的空表头单元格。 */}
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {load.data.jobs.failedRows.map((j) => (
                      <TableRow key={j.id}>
                        <TableCell sx={{ fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>
                          {j.kind}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {j.entityKind} #{j.entityId}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Stack direction="row" spacing={0.75} useFlexGap
                                 sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <span>{j.attempts}</span>
                            {/* ⚠ `resultClass` 的空串是「不知道」，**不是**「正常」。
                                存量失败行（含 dev 上 MNT-000012 的 113 号）全是空串；
                                把它折进 transient，这里就会对着一条其实是 400 的失败
                                说"多半是网络抖动，重试看看"——而那是三次徒劳重试的
                                邀请。判据与色值在 adminSettings.mjs 的 failClassOf。 */}
                            <Chip size="small" variant="outlined"
                                  color={failClassOf(j) === 'permanent' ? 'error'
                                         : failClassOf(j) === 'transient' ? 'warning' : 'default'}
                                  label={t(failClassKey(j))} />
                          </Stack>
                        </TableCell>
                        {/* 原样显示服务端的报错文本：provider 的状态码、超时秒数
                            都在里面，而任何概括都会把它们抹掉。 */}
                        <TableCell sx={{ fontFamily: 'ui-monospace, monospace',
                                         fontSize: '0.75rem', wordBreak: 'break-word' }}>
                          {j.lastError || '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <RequeueButton job={j} t={t} notify={notify} onDone={load.reload} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
              </DataTable>
            )}
          </Paper>

          <LlmSpendPanel t={t} />

          {/* 权威边界必须写在页面上，而不是只写在设计文档里：改得动的只有模型选择，
              admins / jobPosts / 不可逆闩锁永远部署期权威（OQ-B28 二期分键裁定）。 */}
          <Alert severity="info" variant="outlined">{t('admin.settings.authorityNote')}</Alert>

          <WorkHoursPanel t={t} notify={notify} />
        </Stack>
      )}
      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// ── SB-189 片 1 ③（第二百四十批）：LLM 已计价花费（最近 30 天）────────────────
// 读法的全部纪律在 adminSettings.mjs 的 spendView／formatSpendUsd（有单测）：
// 不显示总额、不完整时端出两个计数、小额不印成 $0.00。
// 这里只多一条：**服务端没接库（503 DB_ABSENT）时说「未接线」，不显示 $0**——
// 一个没接线的计量表显示 0，与一个真的零长得一模一样。
function LlmSpendPanel({ t }) {
  const load = useLoad(() => api.adminLlmSpend(), []);
  const view = load.status === 'ready' ? spendView(load.data) : null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('admin.settings.spend.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('admin.settings.spend.intro')}
      </Typography>
      {load.status === 'loading' && <CircularProgress size={20} />}
      {load.status === 'error' && (
        <Alert severity={load.error?.code === 'DB_ABSENT' ? 'info' : 'error'} variant="outlined">
          {t(load.error?.code === 'DB_ABSENT' ? 'admin.settings.spend.absent' : 'admin.settings.error')}
        </Alert>
      )}
      {view?.state === 'ok' && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography variant="h6" component="span">{view.pricedUsdText}</Typography>
            <Typography variant="body2" color="text.secondary" component="span">
              {t('admin.settings.spend.pricedOf', { n: view.calls })}
            </Typography>
          </Stack>
          {!view.complete && (
            <Alert severity="warning" variant="outlined">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('admin.settings.spend.incomplete')}
              </Typography>
              {view.caveats.map((c) => (
                <Typography key={c.key} variant="body2">
                  {t(c.key, { n: c.n, failed: c.failed ?? 0 })}
                </Typography>
              ))}
            </Alert>
          )}
          {view.rows.length > 0 && (
            <DataTable minWidth={560}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.settings.spend.col.model')}</TableCell>
                  <TableCell align="right">{t('admin.settings.spend.col.calls')}</TableCell>
                  <TableCell align="right">{t('admin.settings.spend.col.priced')}</TableCell>
                  <TableCell align="right">{t('admin.settings.spend.col.unpriced')}</TableCell>
                  <TableCell align="right">{t('admin.settings.spend.col.noUsage')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {view.rows.map((r) => (
                  <TableRow key={r.modelId || '(none)'}>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>{r.modelId || '—'}</TableCell>
                    <TableCell align="right">{r.calls}</TableCell>
                    <TableCell align="right">{r.pricedUsdText}</TableCell>
                    <TableCell align="right">{r.unpricedCalls || '—'}</TableCell>
                    <TableCell align="right">{r.noUsageCalls || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          )}
          {/* 口径声明原样显示（英文，来自服务端 core/llm_spend.h）：它描述的正是这张账
              看不见的东西，翻成一句概括就会把「看不见什么」抹掉。 */}
          {view.basis && (
            <Typography variant="caption" color="text.secondary">{view.basis}</Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
}

// ── 第一百一十批 B②：把一条失败的作业按原参数重排 ───────────────────────────
//
// 这是「dedupe_key 永久占位」那个**类**的问题的总出口。病灶：`review_ticket` 第一轮的
// 键不带轮次，而 `dedupe_key` 是 UNIQUE + INSERT OR IGNORE + **行永不删除** ⇒ 失败三次
// 之后，那张工单的预读在产品面上**永远不会再发生**（再次入队一律 AlreadySettled，
// 静静吞掉 + 一条 WARN）。此前唯一的出路是改库。
//
// 这只按钮不认识 `review_ticket`，也不认识任何一种 kind——它只做"按原参数排一条新键
// （原键 + `:m<now_ms>`）的行"。所以下一次某个 kind 掉进同一个坑时，出口已经在这里了。
//
// **`permanent` 要按两下**：那类失败（400/401/403/404/413/422）重试必然徒劳，一条一模
// 一样的请求会被一模一样地拒绝。所以第一下回 409 RETRY_FUTILE，页面把按钮换成
// "仍然重排"——**从此三次徒劳的重试要人显式表态**。而 `unknown`（存量行）不要求这一步：
// 对一件不知道的事要求额外确认，只会让人学会无脑按那个确认。
function RequeueButton({ job, t, notify, onDone }) {
  const [busy, setBusy] = useState(false);
  // 判据在 .mjs：这里只读它的结果。初值取自 resultClass；服务端回 RETRY_FUTILE 时
  // 也翻成 true——两条路都通向同一个"需要显式表态"的状态。
  const [force, setForce] = useState(() => requeueNeedsForce(job));

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.adminRequeueJob(job.id, force);
      notify(t('admin.settings.jobs.requeued', { id: String(r?.jobId ?? '—') }), 'success');
      onDone();
    } catch (e) {
      if (e?.code === 'RETRY_FUTILE') setForce(true);
      notify(t(errKey(e, { byCode: REQUEUE_ERR_KEYS, fallback: 'admin.settings.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size="small" variant="outlined" color={force ? 'warning' : 'primary'}
            disabled={busy} onClick={run}>
      {force ? t('admin.settings.jobs.requeueForce') : t('admin.settings.jobs.requeue')}
    </Button>
  );
}

// ── 第八十五批：工时两参数（需求方裁「没有合理理由与部署行为绑定的配置都应尽量
// 走管理端点」）─────────────────────────────────────────────────────────────
// 这两个值此前既不在配置里也不在页面上：`server.cpp` 挂着一条 TODO 说"以后做成
// stage 配置键"，而 WorkLogStore 一律用出厂值构造，`set_window_days` 连审计都写好了
// 却没有任何人能调它。裁决把那条 TODO 的方向翻了过来——配置烤进镜像，改一个业务
// 参数就要走一次构建+部署，且下次部署把改动覆盖回文件值。
//
// 页面这一侧刻意做的两件事：
//   ① **同时显示出厂值**。只显示当前值的话，"改回默认"这个动作就没有参照
//      （SB-068 那句"我不知道机器上实际生效的是什么"是同一形状）。
//   ② **没改动就按不动**。一次什么都不改的 POST 会在审计里留下一条"配置变更"，
//      而那条记录说的不是真的。
function WorkHoursPanel({ t, notify }) {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [draft, setDraft] = useState({ windowDays: '', dayStartHour: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const data = await api.adminWorkHoursSettings();
      setState({ status: 'ready', data });
      setDraft({ windowDays: String(data.workLogWindowDays),
                 dayStartHour: String(data.workDayStartHour) });
    } catch {
      setState({ status: 'error', data: null });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { problems, canSubmit, payload } =
    workHoursFormState({ ...draft, current: state.data });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.adminSetWorkHoursSettings(payload);
      setState({ status: 'ready', data: { ...state.data, ...r } });
      setDraft({ windowDays: String(r.workLogWindowDays),
                 dayStartHour: String(r.workDayStartHour) });
      notify(t('admin.workHours.saved'), 'success');
    } catch (e) {
      notify(t(errKey(e, { fallback: 'admin.workHours.error' })), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (state.status === 'error') {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 1 }}>{t('admin.workHours.error')}</Alert>
        <Button size="small" onClick={load}>{t('common.retry')}</Button>
      </Paper>
    );
  }

  const d = state.data;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 700 }} variant="subtitle1">{t('admin.workHours.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('admin.workHours.subtitle')}
      </Typography>
      {state.status === 'loading' || !d ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small" type="number" label={t('admin.workHours.windowDays')}
              value={draft.windowDays}
              onChange={(e) => setDraft((x) => ({ ...x, windowDays: e.target.value }))}
              error={Boolean(problems.windowDays)}
              helperText={problems.windowDays
                ? t(`admin.workHours.err.windowDays.${problems.windowDays}`)
                : t('admin.workHours.windowHelp', { n: d.shippedDefaults?.workLogWindowDays ?? 7 })}
              sx={{ maxWidth: 320 }}
            />
            <TextField
              size="small" type="number" label={t('admin.workHours.dayStartHour')}
              value={draft.dayStartHour}
              onChange={(e) => setDraft((x) => ({ ...x, dayStartHour: e.target.value }))}
              error={Boolean(problems.dayStartHour)}
              helperText={problems.dayStartHour
                ? t(`admin.workHours.err.dayStartHour.${problems.dayStartHour}`)
                : t('admin.workHours.dayStartHelp', { n: d.shippedDefaults?.workDayStartHour ?? 9 })}
              sx={{ maxWidth: 320 }}
            />
          </Stack>
          <Box>
            <Button variant="contained" size="small" disabled={!canSubmit || busy} onClick={save}>
              {t('admin.workHours.save')}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
